import React, { useId, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CompareArrows from '@mui/icons-material/CompareArrows';
import HelpOutline from '@mui/icons-material/HelpOutline';
import type { DeviceMetric } from '../../../../services/deviceMetricTypes';
import { metricPresentation } from './metricPresentation';
import { measurementContext, relatedMeasurements } from './relationships';

export default function DeviceMeasurementContext({ metric, fieldPath, catalog, selected, onAdd }: {
  metric: DeviceMetric; fieldPath: string; catalog: DeviceMetric[]; selected: string[]; onAdd: (routeKey: string, fieldPath?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const context = measurementContext(metric, fieldPath);
  if (!context) return null;
  const related = relatedMeasurements(context, catalog);
  const label = metricPresentation(metric).label;
  return <>
    <Tooltip title={`About ${label} measurements`}>
      <IconButton size="small" aria-label={`About ${label} measurements`} aria-haspopup="dialog" aria-expanded={open}
        aria-controls={open ? `${id}-dialog` : undefined} onClick={() => setOpen(true)}
        sx={{ width: 36, height: 36, flexShrink: 0, color: 'text.secondary',
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
        <HelpOutline fontSize="small" />
      </IconButton>
    </Tooltip>
    <Dialog id={`${id}-dialog`} open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
      aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle id={`${id}-title`}>{context.title}</DialogTitle>
      <DialogContent>
        {label !== context.title && <Typography fontWeight={600} sx={{ mb: 1 }}>{label}</Typography>}
        <DialogContentText id={`${id}-description`}>
          {context.description}
        </DialogContentText>
        {related.length > 0 && <>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>{related.map(({ metric: item, fieldPath: relatedField }) =>
            <Button key={item.routeKey} size="small" variant="outlined" startIcon={<CompareArrows />} disabled={selected.includes(item.routeKey)}
              onClick={() => onAdd(item.routeKey, relatedField)} sx={{ maxWidth: '100%', whiteSpace: 'normal', textAlign: 'left' }}>
              {selected.includes(item.routeKey) ? `${metricPresentation(item).label} selected` : `Compare with ${metricPresentation(item).label}`}
            </Button>)}</Stack>
          <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
            Click Visualize to compare separate charts. Each view retains its own values, coverage and selected field.
          </Typography>
        </>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}><Button autoFocus onClick={() => setOpen(false)}>Got it</Button></DialogActions>
    </Dialog>
  </>;
}
